import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDocs,
  limit,
  query,
  runTransaction,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import "./CopyTradingConfirmedOrder.css";

const TIMEFRAMES = ["1m", "2m", "1h", "2h", "1w", "2w"];

const TV_INTERVALS = {
  "1m": "1",
  "2m": "1",
  "1h": "60",
  "2h": "120",
  "1w": "W",
  "2w": "W",
};


function getTradingViewSymbol(pair) {
    const normalized = String(pair || "EURUSD")
      .trim()
      .toUpperCase();
  
    if (normalized.includes(":")) {
      return normalized;
    }
  
    const compact = normalized.replace(/[-/]/g, "");
  
    // Forex pairs: EURUSD, GBPUSD, USDJPY, etc.
    const forexCurrencies = [
      "USD", "EUR", "GBP", "JPY", "CHF",
      "AUD", "CAD", "NZD",
    ];
  
    if (
      compact.length === 6 &&
      forexCurrencies.includes(compact.slice(0, 3)) &&
      forexCurrencies.includes(compact.slice(3, 6))
    ) {
      return `FX:${compact}`;
    }
  
    // Crypto pairs: BTCUSDT, ETHUSDT, etc.
    const knownAssets = [
      "BTC", "ETH", "BNB", "SOL", "XRP", "ADA", "DOGE",
    ];
  
    const base = knownAssets.find((asset) =>
      compact.startsWith(asset)
    );
  
    if (base) {
      const quote = compact.slice(base.length);
  
      if (["USD", "USDT", "USDC", "BUSD"].includes(quote)) {
        return `BINANCE:${base}${quote === "USD" ? "USDT" : quote}`;
      }
    }
  
    return compact;
  }
  

function getDisplayPair(pair) {
  const normalized = String(pair || "BTC/USD")
    .trim()
    .toUpperCase();

  const symbol = normalized.includes(":")
    ? normalized.split(":").pop()
    : normalized;

  if (symbol.includes("-") || symbol.includes("/")) {
    return symbol.split(/[-/]/)[0];
  }

  const quoteCurrencies = [
    "USDT", "USDC", "BUSD", "USD",
    "BTC", "ETH", "EUR", "GBP",
  ];

  for (const quote of quoteCurrencies) {
    if (symbol.endsWith(quote) && symbol.length > quote.length) {
      return symbol.slice(0, -quote.length);
    }
  }

  return symbol;
}

function StaticTradingChart({ pair }) {
  const chartRef = useRef(null);
  const [timeframe, setTimeframe] = useState("1h");

  useEffect(() => {
    if (!chartRef.current) return;

    chartRef.current.innerHTML = "";

    const container = document.createElement("div");
    container.className = "tradingview-widget-container";
    container.style.width = "100%";
    container.style.height = "100%";

    const widgetContainer = document.createElement("div");
    widgetContainer.className =
      "tradingview-widget-container__widget";
    widgetContainer.style.width = "100%";
    widgetContainer.style.height = "100%";

    container.appendChild(widgetContainer);
    chartRef.current.appendChild(container);

    const script = document.createElement("script");
    script.src =
      "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.type = "text/javascript";
    script.async = true;

    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol: getTradingViewSymbol(pair),
      interval: TV_INTERVALS[timeframe] || "60",
      timezone: "Etc/UTC",
      theme: "dark",
      style: "1",
      locale: "en",
      enable_publishing: false,
      allow_symbol_change: true,
      calendar: false,
      support_host: "https://www.tradingview.com",
      backgroundColor: "#0b1220",
      gridColor: "rgba(255, 255, 255, 0.06)",
      hide_top_toolbar: false,
      hide_legend: false,
      save_image: false,
      withdateranges: true,
      details: false,
      hotlist: false,
      show_popup_button: true,
      popup_width: "1000",
      popup_height: "650",
    });

    container.appendChild(script);

    return () => {
      if (chartRef.current) {
        chartRef.current.innerHTML = "";
      }
    };
  }, [pair, timeframe]);

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div className="chart-title">
          <strong>{getDisplayPair(pair)}</strong>
          <span className="chart-live-indicator">
            <span className="live-dot" />
            Live Chart
          </span>
        </div>

        <div className="chart-timeframes">
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf}
              type="button"
              className={timeframe === tf ? "active" : ""}
              onClick={() => setTimeframe(tf)}
            >
              {tf}
            </button>
          ))}
        </div>
      </div>

      <div className="chart-widget-shell">
        <div className="chart-widget-wrapper" ref={chartRef} />
        <div className="chart-price-scale-mask" aria-hidden="true" />
      </div>
    </div>
  );
}

export default function CopyTradingConfirmedOrder() {
  const location = useLocation();
  const navigate = useNavigate();

  const trade = location.state || {};

  // Trader details can arrive as top-level fields or inside trade.trader.
  const selectedTrader = trade.trader || {};
  const pair = trade.pair || trade.symbol || selectedTrader.pair || "BTC/USDT";
  const traderName = trade.traderName || selectedTrader.name || trade.name || "Pro Trader";
  const traderUsername = trade.traderUsername || selectedTrader.username || "";
  const traderAvatar = trade.traderAvatar || selectedTrader.avatar || trade.avatar || trade.image || "";
  const winRate = trade.winRate ?? selectedTrader.win ?? 0;
  const totalTrades = trade.totalTrades ?? selectedTrader.totalTrades ?? 0;
  const followers = trade.followers ?? selectedTrader.followers ?? 0;
  const selectedRiskLimit = trade.riskLimit ?? null;

  const [amount, setAmount] = useState(
    String(trade.budget ?? trade.amount ?? "100")
  );
  const [riskLevel, setRiskLevel] = useState("Medium");
  const [copyEnabled, setCopyEnabled] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Huzuia kubonyeza mara mbili ndani ya component hii.
  const processingRef = useRef(false);

  const handleCopyTrade = async () => {
    if (processingRef.current) return;

    if (!copyEnabled) {
      alert("Please enable copy trading first.");
      return;
    }

    const investment = Number(amount);

    if (
      amount.trim() === "" ||
      !Number.isFinite(investment) ||
      investment < 10
    ) {
      alert("Kiasi cha chini cha kuanza Copy Trading ni 10 USDT.");
      return;
    }

    const currentUsername = (
      trade.username ||
      localStorage.getItem("username") ||
      ""
    ).trim();

    if (!currentUsername) {
      alert("Tafadhali ingia tena kwenye akaunti yako.");
      return;
    }

    processingRef.current = true;
    setProcessing(true);

    try {
      // Tafuta account ya mtumiaji kwenye collection ya users.
      const userQuery = query(
        collection(db, "users"),
        where("username", "==", currentUsername),
        limit(2)
      );

      const userSnapshot = await getDocs(userQuery);

      if (userSnapshot.size !== 1) {
        throw new Error("USER_NOT_FOUND_OR_DUPLICATED");
      }

      const userRef = userSnapshot.docs[0].ref;

      // Tengeneza ID moja ya order kwa jaribio hili.
      const orderRef = doc(collection(db, "copyTrades"));

      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);

        if (!userDoc.exists()) {
          throw new Error("USER_NOT_FOUND");
        }

        const currentBalance = Number(
            userDoc.data().usdtBalanceOther ?? 0
          );
          
          const investmentCents = Math.round(investment * 100);
          const balanceCents = Math.floor(
            (currentBalance + 1e-9) * 100
          );
          
          if (
            !Number.isFinite(currentBalance) ||
            investmentCents > balanceCents
          ) {
            throw new Error("INSUFFICIENT_BALANCE");
          }

        // Kata salio na hifadhi order ndani ya transaction moja.
        transaction.update(userRef, {
          usdtBalanceOther: Number(
            (currentBalance - investment).toFixed(8)
          ),
        });

        transaction.set(orderRef, {
          // User and order details
          username: currentUsername,
          pair,
          amount: investment,
          riskLevel,
          status: "pending",
          createdAt: new Date().toISOString(),

          // Trader information selected on the previous page
          traderId: trade.traderId || selectedTrader.id || null,
          traderName,
          traderUsername,
          traderAvatar,
          traderType: trade.traderType || selectedTrader.type || trade.market || null,
          traderStyle: trade.traderStyle || selectedTrader.style || null,
          traderROI: trade.traderROI ?? selectedTrader.roi ?? null,
          winRate,
          totalTrades,
          followers,
          copiers: trade.copiers ?? selectedTrader.copiers ?? null,
          aum: trade.aum ?? selectedTrader.aum ?? null,
          returnRate: trade.returnRate ?? selectedTrader.ret ?? null,
          traderRisk: trade.traderRisk ?? trade.risk ?? selectedTrader.risk ?? null,
          verified: trade.verified ?? selectedTrader.verified ?? false,

          // Alert setting selected by the user on the previous page
          riskLimit: selectedRiskLimit,
          riskLimitUnit: selectedRiskLimit == null ? null : "percent",
        });
      });

      alert(
        `Copy Trade imethibitishwa!\n` +
        `Pair: ${getDisplayPair(pair)}\n` +
        `Kiasi: ${investment} USDT`
      );
    } catch (error) {
      console.error("Copy trade error:", error);

      if (error.message === "INSUFFICIENT_BALANCE") {
        alert("Salio lako la USDT halitoshi kwa kiasi ulichochagua.");
      } else if (error.message === "USER_NOT_FOUND_OR_DUPLICATED") {
        alert("Akaunti haijapatikana au username haijawa ya kipekee.");
      } else {
        alert("Imeshindikana kuthibitisha Copy Trading. Jaribu tena.");
      }
    } finally {
      processingRef.current = false;
      setProcessing(false);
    }
  };

  return (
    <div className="copy-trading-confirmed-page">
      <div className="page-topbar">
        <button
          type="button"
          className="back-button"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>

        <h2>Copy Trading</h2>
        <div className="topbar-placeholder" />
      </div>

      <div className="copy-trading-layout">
        <main className="copy-trading-main">
          <section className="trader-profile-card">
            <div className="trader-profile-left">
              {traderAvatar ? (
                <img
                  src={traderAvatar}
                  alt={traderName}
                  className="trader-avatar"
                />
              ) : (
                <div className="trader-avatar trader-avatar-placeholder">
                  {traderName.charAt(0).toUpperCase()}
                </div>
              )}

              <div className="trader-profile-info">
                <h3>{traderName}</h3>
                <p>{traderUsername}</p>

                <div className="verified-trader">
                  <span>✓</span> Verified Trader
                </div>
              </div>
            </div>

            <div className="trader-profile-stats">
              <div>
                <strong>{winRate}%</strong>
                <span>Win Rate</span>
              </div>

              <div>
                <strong>{Number(totalTrades).toLocaleString()}</strong>
                <span>Total Trades</span>
              </div>

              <div>
                <strong>{Number(followers).toLocaleString()}</strong>
                <span>Followers</span>
              </div>
            </div>
          </section>

          <StaticTradingChart pair={pair} />

          <section className="live-signal-card">
            <div className="section-heading">
              <div>
                <h3>Signal Preview</h3>
                <p>Sample values shown for {traderName}; not a verified live signal</p>
              </div>

              <span className="signal-live-badge">
                <span className="live-dot" />
                LIVE
              </span>
            </div>

            <div className="signal-details">
              <div className="signal-direction">
                <span className="signal-label">Direction</span>
                <strong className="sell-label">SELL</strong>
              </div>

              <div>
                <span className="signal-label">Confidence</span>
                <strong>82%</strong>
              </div>

              <div>
                <span className="signal-label">Entry Price</span>
                <strong>81,221.00</strong>
              </div>

              <div>
                <span className="signal-label">Stop Loss</span>
                <strong className="loss-price">81,671.35</strong>
              </div>

              <div>
                <span className="signal-label">Take Profit</span>
                <strong className="profit-price">80,482.75</strong>
              </div>
            </div>
          </section>
        </main>

        <aside className="copy-trading-sidebar">
          <button
            type="button"
            className="copy-trade-button"
            onClick={handleCopyTrade}
            disabled={processing}
          >
            {processing ? "Processing..." : "Copy This Trade"}
          </button>

          <section className="order-settings-card">
            <div className="section-heading">
              <div>
                <h3>Copy Settings</h3>
                <p>Configure your copy trade</p>
              </div>
            </div>

            <div className="order-pair-info">
              <span>Trading Pair</span>
              <strong>{getDisplayPair(pair)}</strong>
            </div>

            <div className="form-group">
              <label htmlFor="copy-amount">
                Investment Amount (USDT)
              </label>

              <div className="amount-input-wrapper">
                <span>$</span>
                <input
                  id="copy-amount"
                  type="number"
                  min="10"
                  step="0.01"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  placeholder="Enter amount"
                  disabled={processing}
                />
              </div>
              <small>Minimum investment: 10 USDT</small>
            </div>

            <div className="form-group">
              <label>Risk Level</label>

              <div className="risk-options">
                {["Low", "Medium", "High"].map((level) => (
                  <button
                    key={level}
                    type="button"
                    className={riskLevel === level ? "active" : ""}
                    onClick={() => setRiskLevel(level)}
                    disabled={processing}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>

            <div className="copy-toggle-row">
              <div>
                <strong>Enable Copy Trading</strong>
                <p>Allow trades from this trader to be copied.</p>
              </div>

              <button
                type="button"
                role="switch"
                aria-checked={copyEnabled}
                className={`toggle-switch ${copyEnabled ? "enabled" : ""}`}
                onClick={() => setCopyEnabled((current) => !current)}
                disabled={processing}
              >
                <span />
              </button>
            </div>

            <div className="order-summary">
              <h4>Order Summary</h4>

              <div>
                <span>Trader</span>
                <strong>{traderName}</strong>
              </div>

              <div>
                <span>Pair</span>
                <strong>{getDisplayPair(pair)}</strong>
              </div>

              <div>
                <span>Investment</span>
                <strong>
                  {Number(amount || 0).toLocaleString()} USDT
                </strong>
              </div>

              <div>
                <span>Risk Level</span>
                <strong>{riskLevel}</strong>
              </div>

              <div>
                <span>Maximum Loss Alert</span>
                <strong>{selectedRiskLimit == null ? "Not set" : `${selectedRiskLimit}%`}</strong>
              </div>
            </div>

            <p className="risk-disclaimer">
              Trading involves risk. Review your settings before confirming.
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}